import { LitElement, html, css } from 'lit';
import { money as fmtMoney } from '../../format';
import { customElement, state } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { showToast } from '../../components/ui/toast';
import { t } from '../../i18n';

/** Families (households): group students, set a billing contact, attach family
 *  fees billed once per household, and generate/record those payments. */
@customElement('cadence-families-page')
export class CadenceFamiliesPage extends LitElement {
  @state() private households: any[] = [];
  @state() private loading = true;
  @state() private showCreate = false;
  @state() private newName = '';
  @state() private selectedId = '';
  @state() private detail: any = null;
  @state() private familyFees: any[] = [];
  @state() private memberQuery = '';
  @state() private memberResults: any[] = [];
  @state() private assignFeeId = '';
  @state() private assignName = '';
  @state() private assignType = 'monthly';
  @state() private assignAmount = '';
  @state() private assignCurrency = '';
  @state() private assignDueDay = '1';
  @state() private assignCustom = false; // false = link a shared fee, true = add a custom charge
  @state() private methods: any[] = [];
  // Record-payment dialog state
  @state() private recordingId = '';
  @state() private recordMethodId = '';
  @state() private recordTxn = '';
  @state() private recordNotes = '';
  private debounce = 0;
  private get feeTypes() {
    return [
      { value: 'monthly', label: t('families.feeType.monthly') }, { value: 'yearly', label: t('families.feeType.yearly') },
      { value: 'occasional', label: t('families.feeType.occasional') }, { value: 'event', label: t('families.feeType.event') },
    ];
  }

  // Default currency for a new custom charge: match the family's shared fees, else USD.
  // (A configurable school-wide default currency lands with the Localization settings phase.)
  private get defaultCurrency(): string {
    return this.familyFees[0]?.currency || (this.detail?.fees ?? [])[0]?.currency || 'USD';
  }

  static styles = css`
    :host { display: block; }
    .head { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--space-4); }
    h1 { margin: 0; font-size: 1.6rem; }
    .layout { display: grid; grid-template-columns: 280px 1fr; gap: var(--space-4); align-items: start; }
    @media (max-width: 760px) { .layout { grid-template-columns: 1fr; } }
    .list { display: flex; flex-direction: column; gap: 6px; }
    .fam { text-align: left; background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: var(--radius-md); padding: 10px 12px; cursor: pointer; font: inherit; color: var(--color-text-primary); }
    .fam:hover { border-color: var(--color-primary); }
    .fam[aria-selected='true'] { border-color: var(--color-primary); background: color-mix(in srgb, var(--color-primary) 8%, transparent); }
    .fam .n { font-weight: 600; }
    .fam .m { color: var(--color-text-secondary); font-size: 0.8rem; }
    .panel { display: grid; gap: var(--space-4); }
    .row { display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap; }
    .links { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 4px; }
    .link { background: none; border: none; padding: 0; font: inherit; font-size: 0.82rem; color: var(--color-primary); cursor: pointer; }
    .link:hover { text-decoration: underline; }
    .item { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 9px 12px; border: 1px solid var(--color-border); border-radius: var(--radius-md); }
    .muted { color: var(--color-text-secondary); font-size: 0.82rem; }
    .rm { cursor: pointer; color: var(--color-danger); background: none; border: none; font: inherit; font-size: 0.82rem; }
    .results { display: flex; flex-direction: column; gap: 4px; margin-top: 6px; }
    .res { display: flex; justify-content: space-between; align-items: center; gap: 10px; width: 100%; text-align: left; padding: 8px 10px; border: 1px solid var(--color-border); border-radius: var(--radius-sm); font: inherit; font-size: 0.88rem; color: var(--color-text-primary); background: var(--color-bg-primary); cursor: pointer; transition: border-color 0.15s, background 0.15s; }
    .res:hover { border-color: var(--color-primary); background: color-mix(in srgb, var(--color-primary) 8%, transparent); }
    .res .add { color: var(--color-primary); font-weight: 600; flex-shrink: 0; }
    .badge { font-size: 0.72rem; color: var(--color-primary); border: 1px solid var(--color-primary); border-radius: 999px; padding: 1px 8px; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try {
      const [hh, fees, methods] = await Promise.all([
        api.get<any[]>('/households'), api.get<any[]>('/fees?all=1'), api.get<any[]>('/fees/methods'),
      ]);
      this.households = hh;
      this.familyFees = fees.filter((f) => f.scope === 'household' && f.active);
      this.methods = methods;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private async select(id: string) {
    this.selectedId = id; this.memberQuery = ''; this.memberResults = [];
    this.assignFeeId = ''; this.assignName = ''; this.assignType = 'monthly'; this.assignAmount = ''; this.assignCurrency = ''; this.assignDueDay = '1'; this.assignCustom = false;
    try { this.detail = await api.get<any>(`/households/${id}`); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async reloadDetail() { if (this.selectedId) this.detail = await api.get<any>(`/households/${this.selectedId}`); }

  private async create() {
    if (!this.newName.trim()) return;
    try {
      const hh = await api.post<any>('/households', { name: this.newName.trim() });
      this.newName = ''; this.showCreate = false;
      await this.load(); await this.select(hh.id);
    } catch (e) { showToast(e instanceof ApiError ? e.message : t('families.toast.failed'), 'error'); }
  }
  private async removeFamily() {
    if (!this.detail) return;
    try { await api.del(`/households/${this.detail.id}`); this.detail = null; this.selectedId = ''; await this.load(); showToast(t('families.toast.familyDeleted'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  private searchMembers(value: string) {
    this.memberQuery = value;
    clearTimeout(this.debounce);
    if (value.trim().length < 2) { this.memberResults = []; return; }
    this.debounce = window.setTimeout(async () => {
      try {
        const data = await api.get<{ students: any[] }>(`/students?search=${encodeURIComponent(value)}&limit=6`);
        const memberIds = new Set((this.detail?.members ?? []).map((m: any) => m.id));
        this.memberResults = data.students.filter((s) => !memberIds.has(s.id));
      } catch { /* ignore */ }
    }, 250);
  }
  private async addMember(userId: string) {
    try { await api.post(`/households/${this.selectedId}/members`, { user_id: userId }); this.memberQuery = ''; this.memberResults = []; await this.reloadDetail(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async removeMember(userId: string) { await api.del(`/households/${this.selectedId}/members/${userId}`); await this.reloadDetail(); }
  private async setContact(userId: string) {
    try { await api.put(`/households/${this.selectedId}`, { billing_contact_id: userId }); await this.reloadDetail(); showToast(t('families.toast.contactSet'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async assignFee() {
    let body: Record<string, unknown>;
    if (this.assignCustom) {
      if (!this.assignName.trim() || this.assignAmount === '') { showToast(t('families.toast.enterChargeDetails'), 'error'); return; }
      body = {
        name: this.assignName.trim(), type: this.assignType, amount: Number(this.assignAmount),
        currency: (this.assignCurrency || this.defaultCurrency).trim().toUpperCase(),
        due_day: Number(this.assignDueDay) || null,
      };
    } else {
      if (!this.assignFeeId) { showToast(t('families.toast.chooseFee'), 'error'); return; }
      body = { fee_definition_id: this.assignFeeId };
    }
    try {
      await api.post(`/households/${this.selectedId}/fees`, body);
      this.assignFeeId = ''; this.assignName = ''; this.assignType = 'monthly'; this.assignAmount = ''; this.assignCurrency = ''; this.assignDueDay = '1'; this.assignCustom = false;
      await this.reloadDetail(); showToast(t('families.toast.feeSet'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async removeFee(hfId: string) { await api.del(`/households/${this.selectedId}/fees/${hfId}`); await this.reloadDetail(); }
  private async generate() {
    try { const r = await api.post<{ created: number }>('/payments/generate', {}); showToast(t('families.toast.paymentsGenerated', { count: r.created }), 'success'); await this.reloadDetail(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  /** Open the record dialog, pre-selecting the default (enabled) method. */
  private openRecord(id: string) {
    const enabled = this.methods.filter((m) => m.enabled);
    const def = enabled.find((m) => m.is_default) ?? enabled[0];
    this.recordingId = id;
    this.recordMethodId = def?.id ?? '';
    this.recordTxn = '';
    this.recordNotes = '';
  }
  private closeRecord() { this.recordingId = ''; }
  private async confirmRecord() {
    if (!this.recordMethodId) { showToast(t('families.toast.chooseMethod'), 'error'); return; }
    try {
      await api.post(`/payments/${this.recordingId}/record`, {
        payment_method_id: this.recordMethodId,
        transaction_id: this.recordTxn.trim() || null,
        notes: this.recordNotes.trim() || null,
      });
      showToast(t('families.toast.paymentRecorded'), 'success');
      this.closeRecord();
      await this.reloadDetail();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private money(a: number, c: string) { return fmtMoney(a, c); }
  private payColor(s: string) { return s === 'paid' ? 'var(--color-success)' : s === 'overdue' ? 'var(--color-danger)' : s === 'cancelled' ? 'var(--color-text-secondary)' : 'var(--color-primary)'; }

  private renderDetail() {
    const d = this.detail;
    if (!d) return html`<cadence-empty-state heading=${t('families.list.selectEmptyHeading')} message=${t('families.list.selectEmptyMessage')}></cadence-empty-state>`;
    const noContact = !d.billing_contact_id;
    return html`
      <div class="panel">
        <div class="head">
          <h2 style="margin:0">${d.name}</h2>
          <button class="rm" @click=${() => this.removeFamily()}>${t('families.form.deleteFamily')}</button>
        </div>

        <cadence-card heading=${t('families.members.heading')}>
          ${noContact ? html`<p class="muted">${t('families.members.setContactWarning')}</p>` : ''}
          <div class="list">
            ${(d.members ?? []).map((m: any) => html`
              <div class="item">
                <div>${m.first_name} ${m.last_name} <span class="muted">${m.email}</span>
                  ${m.is_billing_contact ? html`<span class="badge">${t('families.members.billingContactBadge')}</span>` : ''}</div>
                <div class="row" style="gap:8px">
                  ${m.is_billing_contact ? '' : html`<button class="act" style="cursor:pointer;background:none;border:1px solid var(--color-border);border-radius:var(--radius-sm);padding:3px 8px;font:inherit;font-size:0.8rem;color:var(--color-text-primary)" @click=${() => this.setContact(m.id)}>${t('families.members.makeContact')}</button>`}
                  <button class="rm" @click=${() => this.removeMember(m.id)}>${t('families.members.remove')}</button>
                </div>
              </div>`)}
            ${(d.members ?? []).length ? '' : html`<p class="muted">${t('families.members.noMembers')}</p>`}
          </div>
          <div style="margin-top:12px">
            <cadence-input label=${t('families.members.addStudentLabel')} placeholder=${t('families.members.searchPlaceholder')} .value=${this.memberQuery}
              @input=${(e: CustomEvent) => this.searchMembers(e.detail)}></cadence-input>
            ${this.memberResults.length ? html`<div class="results">
              ${this.memberResults.map((s) => html`<button class="res" @click=${() => this.addMember(s.id)}>
                <span>${s.first_name} ${s.last_name} <span class="muted">${s.email}</span></span>
                <span class="add">${t('families.members.add')}</span></button>`)}
            </div>` : ''}
          </div>
        </cadence-card>

        <cadence-card heading=${t('families.fees.heading')}>
          <div class="row" style="margin-bottom:6px">
            ${this.assignCustom
              ? html`<cadence-input label=${t('families.fees.descriptionLabel')} placeholder=${t('families.fees.descriptionPlaceholder')} .value=${this.assignName}
                    @input=${(e: CustomEvent) => (this.assignName = e.detail)}></cadence-input>
                  <cadence-select label=${t('families.fees.typeLabel')} .value=${this.assignType} .options=${this.feeTypes}
                    @change=${(e: CustomEvent) => (this.assignType = e.detail)}></cadence-select>
                  <cadence-input label=${t('families.fees.amountLabel')} type="number" .value=${this.assignAmount}
                    @input=${(e: CustomEvent) => (this.assignAmount = e.detail)}></cadence-input>
                  <cadence-currency-select label=${t('families.fees.currencyLabel')} .value=${this.assignCurrency || this.defaultCurrency}
                    @change=${(e: CustomEvent) => (this.assignCurrency = e.detail)}></cadence-currency-select>
                  <cadence-input label=${t('families.fees.dueDayLabel')} type="number" .value=${this.assignDueDay}
                    @input=${(e: CustomEvent) => (this.assignDueDay = e.detail)}></cadence-input>`
              : html`<cadence-select label=${t('families.fees.familyFeeLabel')} placeholder=${t('families.fees.familyFeePlaceholder')} .value=${this.assignFeeId}
                  .options=${this.familyFees.map((f) => ({ value: f.id, label: `${f.name} (${this.money(f.base_amount ?? 0, f.currency)})` }))}
                  @change=${(e: CustomEvent) => (this.assignFeeId = e.detail)}></cadence-select>`}
            <cadence-button @click=${() => this.assignFee()}>${this.assignCustom ? t('families.fees.addCharge') : t('families.fees.addFee')}</cadence-button>
          </div>
          <div class="links">
            <button class="link" @click=${() => { this.assignCustom = !this.assignCustom; this.assignName = ''; this.assignAmount = ''; this.assignFeeId = ''; }}>
              ${this.assignCustom ? t('families.fees.linkSharedInstead') : t('families.fees.addCustomCharge')}
            </button>
          </div>
          ${this.familyFees.length || this.assignCustom ? '' : html`<p class="muted">${t('families.fees.noShared')}</p>`}
          <div class="list" style="margin-top:12px">
            ${(d.fees ?? []).map((f: any) => html`<div class="item"><div><strong>${f.fee_name}</strong> <span class="muted">${f.hidden ? t('families.fees.customCharge') : f.type} · ${t('families.fees.amountPerPeriod', { amount: this.money(f.amount ?? f.base_amount ?? 0, f.currency) })}</span></div><button class="rm" @click=${() => this.removeFee(f.id)}>${t('families.members.remove')}</button></div>`)}
            ${(d.fees ?? []).length ? '' : html`<p class="muted">${t('families.fees.noneSet')}</p>`}
          </div>
        </cadence-card>

        <cadence-card heading=${t('families.payments.heading')}>
          <div class="row" style="margin-bottom:12px">
            <cadence-button variant="secondary" @click=${() => this.generate()}>${t('families.payments.generateThisMonth')}</cadence-button>
          </div>
          ${(d.payments ?? []).length
            ? html`<cadence-table .columns=${[
                { key: 'due_date', label: t('families.payments.col.due') },
                { key: 'fee_name', label: t('families.payments.col.fee'), render: (r: any) => html`${r.fee_name ?? '—'}` },
                { key: 'amount', label: t('families.payments.col.amount'), render: (r: any) => html`${this.money(r.amount, r.currency)}` },
                { key: 'method_name', label: t('families.payments.col.method'), render: (r: any) => html`${r.status === 'paid' ? (r.method_name ?? '—') : html`<span class="muted">—</span>`}` },
                { key: 'status', label: t('families.payments.col.status'), render: (r: any) => html`<cadence-badge color=${this.payColor(r.status)}>${r.status}</cadence-badge>` },
                { key: 'id', label: '', render: (r: any) => (r.status === 'paid' || r.status === 'cancelled') ? '' : html`<cadence-button variant="ghost" @click=${() => this.openRecord(r.id)}>${t('families.payments.record')}</cadence-button>` },
              ]} .rows=${d.payments}></cadence-table>`
            : html`<cadence-empty-state heading=${t('families.payments.emptyHeading')} message=${t('families.payments.emptyMessage')}></cadence-empty-state>`}
        </cadence-card>
      </div>`;
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    return html`
      <div class="head">
        <h1>${t('families.list.title')}</h1>
        <cadence-button @click=${() => (this.showCreate = !this.showCreate)}>${t('families.list.newFamily')}</cadence-button>
      </div>
      ${this.showCreate ? html`<div class="row" style="margin-bottom:var(--space-4)">
        <cadence-input label=${t('families.form.nameLabel')} placeholder=${t('families.form.namePlaceholder')} .value=${this.newName} @input=${(e: CustomEvent) => (this.newName = e.detail)}></cadence-input>
        <cadence-button @click=${() => this.create()}>${t('families.form.create')}</cadence-button>
        <cadence-button variant="ghost" @click=${() => (this.showCreate = false)}>${t('common.actions.cancel')}</cadence-button>
      </div>` : ''}

      <div class="layout">
        <div class="list">
          ${this.households.map((h) => html`
            <button class="fam" aria-selected=${this.selectedId === h.id ? 'true' : 'false'} @click=${() => this.select(h.id)}>
              <div class="n">${h.name}</div>
              <div class="m">${h.member_count === 1 ? t('families.list.memberOne', { count: h.member_count }) : t('families.list.memberMany', { count: h.member_count })}${h.contact_name ? ` · ${h.contact_name}` : ` · ${t('families.list.noContact')}`}</div>
            </button>`)}
          ${this.households.length ? '' : html`<p class="muted">${t('families.list.noFamilies')}</p>`}
        </div>
        <div>${this.renderDetail()}</div>
      </div>

      ${this.recordingId ? this.renderRecordModal() : ''}
    `;
  }

  private renderRecordModal() {
    const p = (this.detail?.payments ?? []).find((x: any) => x.id === this.recordingId);
    const enabled = this.methods.filter((m) => m.enabled);
    return html`
      <cadence-modal open heading=${t('families.payments.recordModalHeading')} @close=${() => this.closeRecord()}>
        ${p ? html`<p class="muted" style="margin-top:0">${p.fee_name ?? t('families.payments.paymentFallback')} · <strong>${this.money(p.amount, p.currency)}</strong></p>` : ''}
        <div style="display:flex;flex-direction:column;gap:12px">
          <cadence-select label=${t('families.payments.paidVia')} .value=${this.recordMethodId}
            .options=${enabled.map((m) => ({ value: m.id, label: m.is_default ? t('families.payments.methodDefault', { name: m.name }) : m.name }))}
            @change=${(e: CustomEvent) => (this.recordMethodId = e.detail)}></cadence-select>
          <cadence-input label=${t('families.payments.referenceLabel')} placeholder=${t('families.payments.referencePlaceholder')} .value=${this.recordTxn} @input=${(e: CustomEvent) => (this.recordTxn = e.detail)}></cadence-input>
          <cadence-input label=${t('families.payments.notesLabel')} .value=${this.recordNotes} @input=${(e: CustomEvent) => (this.recordNotes = e.detail)}></cadence-input>
        </div>
        ${enabled.length ? '' : html`<p class="muted" style="color:var(--color-danger)">${t('families.payments.noEnabledMethods')}</p>`}
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => this.closeRecord()}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button ?disabled=${!enabled.length} @click=${() => this.confirmRecord()}>${t('families.payments.recordPayment')}</cadence-button>
        </div>
      </cadence-modal>`;
  }
}
